import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Key } from 'w3c-keys';
import { RD_COMPONENTS } from '../../_utils';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { InputBase, } from '../input-base';
import { InputVariant } from '../input-base/input-base';
import styles from './input-number.module.scss';
export const regexPattern = /^-?[0-9]*\.[0-9]*0$|^-?[0-9]*\.$/;
function defaultParser(input) {
    // Japanese IME in full-width mode emits digits as Unicode fullwidth characters
    // (U+FF10 '０' – U+FF19 '９'). The \w character class only covers ASCII, so
    // those characters would be stripped by the replace below and the field would
    // appear to ignore input. Convert them to their ASCII equivalents first so
    // they are treated as ordinary digit characters throughout the pipeline.
    const normalized = input.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff10 + 0x30));
    return normalized.replace(/[^\w.-]+/g, '');
}
function getPresetRange(preset) {
    switch (preset) {
        case 'INT32':
            return { min: -1 * 2 ** 31, max: 2 ** 31 - 1 };
        case 'INT64':
            return {
                /**
                 * Actual `int64` range is larger.
                 * But, w/o custom handling of integers across the product,
                 *   this is the largest integer range JS can safely handle.
                 */
                min: Number.MIN_SAFE_INTEGER,
                max: Number.MAX_SAFE_INTEGER,
            };
        /**
         * https://learn.microsoft.com/en-us/cpp/c-language/type-float?view=msvc-170
         *
         * FLOAT: Bias - 127, Mantissa - 24
         * DOUBLE: Bias - 1023, Mantissa - 53
         *
         * Based on Java and JS docs,
         * - min pos. value = 2 ** -(Bias - 1 + Mantissa - 1)
         * - max pos. value = 2 ** (Bias + 1) - 2 ** (Bias - Mantissa + 1)
         */
        case 'FLOAT':
            return {
                min: -1 * (2 ** 128 - 2 ** 104),
                max: 2 ** 128 - 2 ** 104,
            };
        case 'DOUBLE':
            return { min: -Number.MAX_VALUE, max: Number.MAX_VALUE };
        default:
            return { min: -Infinity, max: Infinity };
    }
}
export const InputNumber = ({ ariaLabel, id, className, name, disabled, precision, value = '', onChange = _.noop, parser = defaultParser, formatter, onBlur, rangePreset, min = -Infinity, max = Infinity, step = 1, allowSpinner = true, spinnerIconSize = IconSize.MEDIUM, readOnly = false, isAutoWidth = false, preventArrowKeyDefault = false, ...restProps }) => {
    const { inputValueMin, inputValueMax } = useMemo(() => {
        const { min: presetMin, max: presetMax } = getPresetRange(rangePreset);
        return {
            inputValueMin: rangePreset !== undefined && min === -Infinity
                ? presetMin
                : min,
            inputValueMax: rangePreset !== undefined && max === Infinity ? presetMax : max,
        };
    }, [rangePreset, min, max]);
    const getFormattedValue = useCallback((val, round) => {
        let formattedValue = val;
        if (round &&
            typeof val === 'number' &&
            typeof precision === 'number' &&
            precision >= 0) {
            formattedValue = val.toFixed(precision);
        }
        if (formatter) {
            formattedValue = formatter(formattedValue);
        }
        return formattedValue;
    }, [formatter, precision]);
    const convertValueToNumber = useCallback((val) => {
        if (typeof val === 'string') {
            const parsedVal = parser(val);
            const convertedVal = parseFloat(parsedVal);
            return typeof precision === 'number' && precision >= 0
                ? +convertedVal.toFixed(precision)
                : +convertedVal;
        }
        return val;
    }, [parser, precision]);
    const getValidValue = useCallback((val) => {
        if (typeof val === 'string' && val[val.length - 1] === '.') {
            return val;
        }
        // SCAL-119099 Decimal value support in conditional formatting eg:(0.01)
        if (typeof val === 'string' && regexPattern.test(val.toString())) {
            return val;
        }
        let validValue = convertValueToNumber(val);
        // eslint-disable-next-line no-restricted-globals
        if (!isNaN(validValue)) {
            validValue = Math.min(Math.max(validValue, inputValueMin), inputValueMax);
            return validValue;
        }
        return '';
    }, [convertValueToNumber, inputValueMax, inputValueMin]);
    const [inputValue, setInputValue] = useState(() => {
        const validatedValue = getValidValue(value);
        return getFormattedValue(validatedValue, true);
    });
    const inputId = id || (name ? `input-${name}` : undefined);
    useEffect(() => {
        if (value !== inputValue) {
            if (value === '-' || value === '-0') {
                setInputValue(value);
            }
            else {
                const validatedValue = getValidValue(value);
                const formattedValue = getFormattedValue(validatedValue, true);
                setInputValue(formattedValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, getFormattedValue, getValidValue]);
    const handleChange = (val, round) => {
        if (val === '-' || val === '-0') {
            setInputValue(val);
            onChange(val);
        }
        else {
            const validatedValue = getValidValue(val);
            const formattedValue = getFormattedValue(validatedValue, round);
            setInputValue(formattedValue);
            onChange(formattedValue);
        }
    };
    /* Converts to decimal when precision is passed
       or if the number is already in decimal then fixes to the precision of original value. This is required
       since float addition/subtraction in JS does not round off correctly in some cases.
       Returns the same value if no decimal conversion required */
    const convertToDecimalWithPrecision = (originalValue, updatedValue) => {
        let convertedValue = updatedValue;
        const isDecimal = originalValue.toString().includes('.');
        if (typeof precision === 'number' && precision >= 0) {
            convertedValue = +updatedValue.toFixed(precision);
        }
        else if (isDecimal) {
            const decimalPlaces = originalValue.toString().split('.')[1].length;
            convertedValue = +updatedValue.toFixed(decimalPlaces);
        }
        return convertedValue;
    };
    const onSpinnerUpButtonClick = () => {
        const val = convertValueToNumber(inputValue) || 0;
        if (val < inputValueMax) {
            let updatedValue = +(val + step);
            updatedValue = convertToDecimalWithPrecision(val, updatedValue);
            const maxVal = Math.min(updatedValue, inputValueMax);
            handleChange(maxVal, true);
        }
    };
    const onSpinnerDownButtonClick = () => {
        const val = convertValueToNumber(inputValue) || 0;
        if (val > inputValueMin) {
            let updatedValue = val - step;
            updatedValue = convertToDecimalWithPrecision(val, updatedValue);
            const minVal = Math.max(updatedValue, inputValueMin);
            handleChange(minVal, true);
        }
    };
    const onInputChange = (event, isComposing) => {
        if (isComposing) {
            // IME composition is in progress. Display the raw preedit characters
            // so the user sees live feedback from the IME overlay, but do not
            // run parse/validate logic or call onChange yet — intermediate preedit
            // text is not a valid or final value. The committed value will be
            // processed in handleCompositionEnd once the IME session closes.
            setInputValue(event.target.value);
            return;
        }
        const parsedVal = parser(event.target.value);
        handleChange(parsedVal, false);
    };
    const handleCompositionEnd = (event) => {
        const parsedVal = parser(event.currentTarget.value);
        // Guard against an empty parsedVal before calling handleChange.
        // This can happen when the IME commits a character that the parser
        // cannot represent as a number — for example, non-numeric kana/kanji,
        // or full-width digits that were not already converted by defaultParser.
        // Calling handleChange('') would fire onChange(''), which callers
        // typically store as Number('') = 0, potentially violating the field's
        // minimum constraint and triggering a server-side validation error.
        // Skipping the call here lets the useEffect restore the display to the
        // last valid formatted value from the controlled `value` prop instead.
        if (parsedVal !== '') {
            handleChange(parsedVal, false);
        }
    };
    const handleBlur = (event) => {
        const formattedValue = getFormattedValue(getValidValue(event.target.value), true);
        setInputValue(formattedValue);
        if (onBlur) {
            onBlur(event);
        }
    };
    const handleKeyDown = (event) => {
        if (event.key === Key.ArrowUp) {
            if (preventArrowKeyDefault) {
                event.preventDefault();
            }
            onSpinnerUpButtonClick();
        }
        if (event.key === Key.ArrowDown) {
            if (preventArrowKeyDefault) {
                event.preventDefault();
            }
            onSpinnerDownButtonClick();
        }
    };
    const getSpinnerIcon = (iconId) => (React.createElement(Icon, { className: styles.spinnerButtonIcon, color: IconColor.TEXT_COLOR, size: spinnerIconSize, id: iconId }));
    const getSpinnerMarkup = () => !readOnly && (React.createElement("div", { className: styles.spinnerButtonsWrap },
        React.createElement("button", { type: "button", "aria-label": "Increase Value", onClick: onSpinnerUpButtonClick, disabled: disabled, className: cx(styles.spinnerUpButton, styles.spinnerButton) }, getSpinnerIcon(IconID.CHEVRON_UP)),
        React.createElement("button", { type: "button", "aria-label": "Decrease Value", disabled: disabled, onClick: onSpinnerDownButtonClick, className: cx(styles.spinnerDownButton, styles.spinnerButton) }, getSpinnerIcon(IconID.CHEVRON_DOWN))));
    const containerClassNames = cx(className, styles.container);
    const containerInputNumber = cx(styles.inputNumber, {
        [styles.isAutoWidth]: isAutoWidth,
    });
    return (React.createElement("div", { "data-radiant-v2": true, className: containerClassNames },
        React.createElement(InputBase, { role: "spinbutton", autoComplete: "off", name: name, value: inputValue, className: containerInputNumber, "aria-label": ariaLabel, id: inputId, disabled: disabled, onBlur: handleBlur, onChange: onInputChange, onCompositionEnd: handleCompositionEnd, suffix: allowSpinner ? getSpinnerMarkup() : null, "aria-valuenow": inputValue, "aria-valuemin": inputValueMin, "aria-valuemax": inputValueMax, readOnly: readOnly, variant: InputVariant.NUMBER, isFormatterPresent: !!formatter, onKeyDown: handleKeyDown, ...restProps })));
};
InputNumber.RD_COMPONENT = RD_COMPONENTS.INPUT_NUMBER;
//# sourceMappingURL=input-number.js.map